// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  src/utils/safety.js                                                       ║
// ║  Helper centralizzati di sicurezza per Bottega del Mare.                   ║
// ║  Nessuna dipendenza esterna: solo API browser-native.                      ║
// ║                                                                            ║
// ║  Contenuto:                                                                ║
// ║    - escapeHtml(s)            -> sanitizza testo verso contesto HTML       ║
// ║    - sniffMime(file)          -> rileva MIME reale via magic bytes         ║
// ║    - readAsDataURL(file)      -> FileReader promesso                       ║
// ║    - processAttachments(...)  -> pipeline completa di upload allegati      ║
// ║    - isValidE164(s)           -> validazione numero telefonico             ║
// ║    - ALLOWED_ATTACHMENT_MIMES                                              ║
// ║    - MAX_ATTACHMENT_SIZE_MB / MAX_TOTAL_ATTACHMENTS_MB                     ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// ── HTML escaping (OWASP XSS Prevention Cheat Sheet) ──
export const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s).replace(/[&<>"'/]/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  }[c]));
};

// ── MIME consentiti per allegati DDT / Fatture ──
export const ALLOWED_ATTACHMENT_MIMES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
]);

// ── Limiti dimensione (in MB) ──
export const MAX_ATTACHMENT_SIZE_MB = 10;       // singolo file
export const MAX_TOTAL_ATTACHMENTS_MB = 25;     // cumulativo per record (DDT o fattura)

// ── Magic bytes sniff (primi 12 byte) ──
// Riferimento: https://en.wikipedia.org/wiki/List_of_file_signatures
export const sniffMime = async (file) => {
  try {
    const buf = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const hex = Array.from(buf).map(b => b.toString(16).padStart(2, "0")).join(" ");
    if (hex.startsWith("89 50 4e 47")) return "image/png";
    if (hex.startsWith("ff d8 ff")) return "image/jpeg";
    if (hex.startsWith("25 50 44 46")) return "application/pdf";
    return null;
  } catch (_e) {
    return null;
  }
};

// ── FileReader -> dataUrl (con error handling) ──
export const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error("FileReader error"));
  reader.readAsDataURL(file);
});

// ── Pipeline allegati: valida + legge in dataUrl ──
// Ritorna { valid:[{name,size,type,dataUrl,addedAt}], rejected:[{name,reason}] }
export const processAttachments = async (files, existingAttachments = [], opts = {}) => {
  const {
    maxFiles = 10,
    maxSizeMb = MAX_ATTACHMENT_SIZE_MB,
    maxTotalMb = MAX_TOTAL_ATTACHMENTS_MB,
  } = opts;

  const fileArr = Array.from(files || []).slice(0, maxFiles);
  const valid = [];
  const rejected = [];
  let totalSize = (existingAttachments || []).reduce((s, a) => s + (a.size || 0), 0);

  for (const f of fileArr) {
    // 1. Cap dimensione singolo file
    if (f.size > maxSizeMb * 1024 * 1024) {
      rejected.push({ name: f.name, reason: `Troppo grande (max ${maxSizeMb} MB)` });
      continue;
    }
    // 2. Sniff MIME reale (NON ci fidiamo del Content-Type del browser)
    const sniffed = await sniffMime(f);
    if (!sniffed || !ALLOWED_ATTACHMENT_MIMES.has(sniffed)) {
      rejected.push({ name: f.name, reason: "Tipo non consentito (solo PDF, PNG, JPEG)" });
      continue;
    }
    // 3. Cap cumulativo per record
    if (totalSize + f.size > maxTotalMb * 1024 * 1024) {
      rejected.push({ name: f.name, reason: `Spazio totale superato (max ${maxTotalMb} MB per record)` });
      continue;
    }
    // 4. Lettura come dataUrl
    try {
      const dataUrl = await readAsDataURL(f);
      valid.push({
        name: f.name,
        size: f.size,
        type: sniffed,
        dataUrl,
        addedAt: new Date().toISOString(),
      });
      totalSize += f.size;
    } catch (_e) {
      rejected.push({ name: f.name, reason: "Lettura file fallita" });
    }
  }

  return { valid, rejected };
};

// ── Validazione telefono E.164 ──
export const isValidE164 = (s) => /^\+?[1-9]\d{7,14}$/.test(String(s || "").replace(/\s/g, ""));

// ─── Audit log helper ───────────────────────────────────────────────
// Pattern append-only: aggiunge un evento allo state.auditLog senza mutare.
// Cap a 5000 eventi (FIFO) per non saturare localStorage.
// Schema entry: { id, at, by, role, action, entityType, entityId, before?, after?, reason? }
//
// Riferimento normativo:
//  - Codice Civile art. 2220: conservazione 10 anni delle scritture contabili
//  - DPR 633/72 art. 39: registri IVA conservati 10 anni
//  - Reg. CE 178/2002 art. 18: tracciabilità "one step back/forward" verificabile
//
// L'audit log NON deve mai essere editabile/cancellabile dalla UI.
export const MAX_AUDIT_ENTRIES = 5000;

export const appendAudit = (state, event) => {
  const entry = {
    id: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    by: state?.currentUser?.name || "Sconosciuto",
    role: state?.currentUser?.role || null,
    ...event,
  };
  const prev = Array.isArray(state?.auditLog) ? state.auditLog : [];
  const next = [...prev, entry];
  // FIFO cap: tieni solo gli ultimi MAX_AUDIT_ENTRIES
  return next.length > MAX_AUDIT_ENTRIES ? next.slice(-MAX_AUDIT_ENTRIES) : next;
};

// ─── Soft-delete helpers ────────────────────────────────────────────
// Marca un record come archiviato senza rimuoverlo (conservazione legale).
// Per ripristinare, rimuovere i campi deletedAt/deletedBy/deletedReason.
export const softDelete = (entity, by, reason) => ({
  ...entity,
  deletedAt: new Date().toISOString(),
  deletedBy: by || "Sconosciuto",
  deletedReason: reason || null,
});

// Filtro per nascondere i soft-deleted dalle liste attive.
export const isActive = (e) => !e?.deletedAt;
